import React, { memo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Paper, Avatar, Divider, CircularProgress, Link } from '@mui/material';
import {  useSelector } from 'react-redux';
import { ROUTE_FOR_ASSETS, ROUTES } from '../../../../constants/routes';
import { ReactComponent as ArrowInCircleIcon } from '../../../../assets/images/arrow-in-cirlce.svg';
import { BoxInfo } from '../BoxInfo';
import { DoubleField } from '../DoubleField';
import { maskNumberPhone } from '../../../../utils/maskNumberPhone';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import { CustomButton } from '../../../../components/CustomButton';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { DISABLED_CONTROL_TOOLTIP, PERMISSIONS } from '../../../../constants/variables';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';
import { useSearchCustomerBy } from '../../../../hooks/useSearchCustomerBy';

const returnYesOrNo = (boolean) => (boolean ? 'Да' : 'Нет');

const CustomerPaper = () => {
  const { createPath } = useSearchCustomerBy();
  const { customer, fetchingCustomer, balance, balanceFetching, bonuses, bonusesFetching } = useSelector((state) => state.support);
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const isDisabledCreateSupportBonusRequests = !visibleBySectionPermission(permissions, PERMISSIONS.SUPPORT.CreateSupportBonusRequests.value);

  const getCustomerName = () => {
    if (!customer) return '';

    const { name, first_name, middle_name, last_name } = customer;

    return name || [first_name, middle_name, last_name].filter((name) => !!name).join(' ').trim();
  };

  return (
    <Paper
      sx={{
        marginTop: '16px',
        padding: '32px',
        display: 'grid',
        gridTemplateColumns: fetchingCustomer || balanceFetching || bonusesFetching ? '1fr' : '128px auto minMax(200px, 254px)',
        gridColumnGap: '32px',
        '@media (max-width: 1078px)': {
          gridTemplateColumns: '1fr',
        },
      }}>
      {fetchingCustomer || balanceFetching || bonusesFetching ? (
        <Box height="340px" display="flex" alignItems="center" justifyContent="center">
          <CircularProgress data-testid="CircularProgress"/>
        </Box>
      ) : (
        <>
          <Avatar
            src={`${ROUTE_FOR_ASSETS}`}
            sx={{ width: 128, height: 128 }}
          />
          <Box>
            <Typography
              variant="h4"
              sx={{ marginBottom: '24px', fontSize: '32px', fontWeight: 500 }}
              data-testid="CustomerName"
            >
              {getCustomerName()}
            </Typography>
            <CustomButton
              component={RouterLink}
              to={createPath(ROUTES.SUPPORT_CREATE_APPLICATION)}
              color="secondary"
              variant="outlined"
              size="small"
              startIcon={<ArrowInCircleIcon />}
              disabled={isDisabledCreateSupportBonusRequests}
              tooltip={isDisabledCreateSupportBonusRequests ? DISABLED_CONTROL_TOOLTIP : null}
              data-testid="CreateApplication"
            >
              Создать заявку
            </CustomButton>
            <Divider sx={{ margin: '24px 0px 16px 0px' }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gridRowGap: '32px' }}>
              <DoubleField title="ИИН" description={customer?.iin} />

              <Box data-testid="MobilePhone">
                <Typography sx={{ color: 'text.secondary', minWidth: 180 }}>
                  Номер телефона
                </Typography>
                <Typography sx={{ fontWeight: 500, fontSize: '20px' }}>
                  {maskNumberPhone(customer?.mobile_phone)}
                  <Typography component="span" sx={{ verticalAlign: 'middle' }}>
                    { customer?.mobile_phone_status && <GppGoodOutlinedIcon/> }
                  </Typography>
                </Typography>
              </Box>

              <Box data-testid="Email">
                <Typography variant="body1" color="text.secondary">
                  Email
                </Typography>

                <Link
                  href="mailto:myemail@gmail.com"
                  variant="subtitle2"
                  color="primary.main"
                  underline="hover"
                  fontWeight="500"
                >
                  {customer?.email}
                </Link>
                <Typography component="span" sx={{ verticalAlign: 'middle' }}>
                  { customer?.email_status && <GppGoodOutlinedIcon/> }
                </Typography>
              </Box>

              <DoubleField
                title="Подключение OTT услуг"
                data-testid="OTT"
                description={returnYesOrNo(customer?.ott_services_status)}
              />

              <DoubleField
                title="Участие в бонусной программе"
                description={returnYesOrNo(bonuses?.bonus_status)}
              />
            </Box>

            <DoubleField
              title="Наличие задолженности по услугам постоплаты"
              data-testid="debt"
              description={balance[0]?.debt && balance[0]?.debt > 0 ? 'Да' : 'Нет'}
              sx={{ marginTop: '16px' }}
            />
          </Box>
          <Box>
            <BoxInfo
              title="Основной ЛС абонента"
              sum={balance[0]?.balance}
              prefixSum={'₸'}
              idNumber={balance[0]?.id}
              titleModal="Все лицевые счета абонента"
              debt={balance[0]?.debt}
              labelTable={[
                'Номер ЛС абонента',
                'Баланс ЛС абонента',
                'Наличие задолженности по услугам постоплаты'
              ]}
              contentTable={balance}
            />

            {bonuses?.bonus_status &&
              <BoxInfo
                sx={{ marginTop: '8px' }}
                title="Основной БС абонента"
                sum={bonuses.bonus_account_balance || '0'}
                prefixSum={'бонусов'}
                hiddenModal={true}
                hiddenDebt={true}
                data-testid="Bonuses"
              />
            }
          </Box>
        </>
      )}
    </Paper>
  );
};

export default memo(CustomerPaper);
